import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import * as React from "react";

import { useAuth } from "@/providers";
import { DatasetSelector } from "@/shared/components/DatasetSelector";
import { useNotification } from "@/shared/components/notifications/useNotification";
import type { ImportFileRequest } from "@/shared/domain/files";
import { useImportFilesMutation } from "@/shared/domain/files/hooks";
import type { S3ObjectEntry } from "@/shared/domain/integrations";
import { Button } from "@/shared/foundation/primitives";
import { formatByteSize, pluralize } from "@/shared/text";

import styles from "./ImportObjectsDialog.module.css";
import { normalizeRelativePath } from "./prefixPath";

/** The id `DatasetSelector` puts on its input, so the visible label can point at it. */
const DATASET_INPUT_ID = "available-datasets";

/** The server's own ceiling on one batch. */
const MAX_BATCH_SIZE = 500;

interface ImportObjectsDialogProps {
  bucketName: string;
  orgId: string;
  /** The objects to import. An empty list keeps the dialog closed. */
  entries: S3ObjectEntry[];
  onClose: () => void;
  /** Called once the import lands, so the page can drop what it just imported. */
  onImported: () => void;
}

/**
 * Import the selected objects into one dataset, by reference.
 *
 * Every path is editable because the S3 key that makes sense in a customer's bucket often does
 * not inside a dataset. Each defaults to the object's full key, which preserves the customer's
 * own layout and is what a backfill usually wants.
 */
export function ImportObjectsDialog({
  bucketName,
  orgId,
  entries,
  onClose,
  onImported,
}: ImportObjectsDialogProps) {
  const { currentOrganization } = useAuth();
  const showNotification = useNotification();
  const importFiles = useImportFilesMutation();

  const [datasetId, setDatasetId] = React.useState<string | null>(null);
  const [pathByKey, setPathByKey] = React.useState<Record<string, string>>({});

  const entryKeys = entries.map((entry) => entry.key).join("\n");
  React.useEffect(
    function resetForNewSelection() {
      setDatasetId(null);
      setPathByKey(
        Object.fromEntries(entryKeys.split("\n").map((key) => [key, key])),
      );
      importFiles.reset();
    },
    // Resets with the selection it is importing, so a half-filled form is never carried over.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [entryKeys],
  );

  if (entries.length === 0) {
    return null;
  }

  const requests: ImportFileRequest[] = entries.map((entry) => ({
    dataset_id: datasetId ?? "",
    relative_path: normalizeRelativePath(pathByKey[entry.key] ?? entry.key),
    uri: `s3://${bucketName}/${entry.key}`,
    size: entry.size ?? 0,
  }));

  const duplicatePaths = requests
    .map((request) => request.relative_path)
    .filter((path, index, paths) => paths.indexOf(path) !== index);

  const problem = describeProblem(requests, duplicatePaths, entries.length);
  const canSubmit =
    datasetId !== null &&
    datasetId.length > 0 &&
    problem === null &&
    !importFiles.isPending;

  const handleClose = () => {
    // The request is not cancellable, so closing mid-flight would leave the operator with no
    // word either way about files that are being written.
    if (importFiles.isPending) {
      return;
    }
    onClose();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canSubmit) {
      return;
    }

    importFiles
      .mutateAsync({ requests, orgId })
      .then((imported) => {
        showNotification(
          `Imported ${imported.length} ${pluralize("file", imported.length)} into ${datasetId}`,
        );
        onImported();
        onClose();
      })
      .catch(() => {
        // The mutation carries its own error; the alert below renders it.
      });
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Import {entries.length} {pluralize("file", entries.length)}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent className={styles.content}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={DATASET_INPUT_ID}>
              Dataset
            </label>
            <DatasetSelector
              datasetId={datasetId}
              setDatasetId={setDatasetId}
              currentOrg={currentOrganization}
            />
          </div>

          <div className={styles.rows}>
            {entries.map((entry) => (
              <div key={entry.key} className={styles.row}>
                <div className={styles.source}>
                  <span className={styles.uri} title={entry.key}>
                    {entry.key}
                  </span>
                  <span className={styles.size}>
                    {formatByteSize(entry.size ?? 0)}
                  </span>
                </div>
                <TextField
                  label="Roboto path"
                  value={pathByKey[entry.key] ?? entry.key}
                  onChange={(event) =>
                    setPathByKey((paths) => ({
                      ...paths,
                      [entry.key]: event.target.value,
                    }))
                  }
                  size="small"
                  fullWidth
                />
              </div>
            ))}
          </div>

          {problem !== null && <Alert severity="warning">{problem}</Alert>}

          {importFiles.error !== null && (
            <Alert severity="error">{importFiles.error.message}</Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="text"
            disabled={importFiles.isPending}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            loading={importFiles.isPending}
          >
            Import
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** The reason this batch cannot be sent, or `null` when it can. */
function describeProblem(
  requests: ImportFileRequest[],
  duplicatePaths: string[],
  selectedCount: number,
): string | null {
  if (selectedCount > MAX_BATCH_SIZE) {
    return `Select at most ${MAX_BATCH_SIZE} files; this batch has ${selectedCount}.`;
  }
  if (requests.some((request) => request.relative_path.length === 0)) {
    return "Every file needs a path inside the dataset.";
  }
  if (duplicatePaths.length > 0) {
    return `Two files share the path "${duplicatePaths[0]}". A dataset holds one file per path.`;
  }
  return null;
}
